/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Spin, FloatButton } from 'antd'
import { ArrowUpOutlined } from '@ant-design/icons'
import { connect } from 'react-redux'
import dayjs from 'dayjs'
import {
  getGameStatusTypesBySportId,
  getAllGameTypes,
} from '@sportninja/common/selectors/types'
import { useMyScheduleContext } from 'src/context/MyScheduleContext'
import FilterBar from '../components/FilterBar'
import Text from 'src/components/ts-components/Text'
import { DayOfGames } from 'src/components/List/GameList'
import { Mobile } from 'src/components/Responsive'

const SCROLL_THRESHOLD = 500

/**
 * MyScheduleList component
 * Displays the user's schedule with filters and game list
 */
const MyScheduleList = ({ gameStatusTypes, gameTypes }) => {
  const {
    games,
    loadingGames,
    loadingDates,
    datesLoaded,
    filtersLoaded,
    gamesPagination,
    fetchMoreGames,
  } = useMyScheduleContext()

  const [isAppending, setIsAppending] = useState(false)
  const isAppendingRef = useRef(false)

  // Calculate if there are more pages to load
  const hasMore =
    gamesPagination &&
    gamesPagination.current_page < gamesPagination.total_pages

  // Load more games function
  const loadMoreGames = useCallback(async () => {
    if (loadingGames || isAppendingRef.current || !hasMore) {
      return
    }

    setIsAppending(true)
    isAppendingRef.current = true
    try {
      await fetchMoreGames()
    } catch (error) {
      console.error('🚨 Error loading more games:', error)
    } finally {
      setIsAppending(false)
      isAppendingRef.current = false
    }
  }, [loadingGames, hasMore, fetchMoreGames])

  // Handle scroll event
  const handleScroll = useCallback(() => {
    const { scrollTop, scrollHeight, clientHeight } = document.documentElement
    const nearBottom =
      scrollHeight - (scrollTop + clientHeight) < SCROLL_THRESHOLD
    if (nearBottom && hasMore && !loadingGames && !isAppendingRef.current) {
      loadMoreGames()
    }
  }, [hasMore, loadingGames, loadMoreGames])

  // Attach scroll listener
  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [handleScroll])

  // Show loading while filters are being loaded
  if (!filtersLoaded) {
    return (
      <div
        css={css`
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 400px;
        `}
      >
        <Spin size='small' />
      </div>
    )
  }

  // Group games by day
  let dates = {}
  games.forEach((item) => {
    const day = dayjs(item.starts_at).format('dddd, MMM D, YYYY')
    dates[day] = dates[day] || []
    dates[day].push(item)
  })

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        width: 100%;
      `}
    >
      {/* Filter Bar */}
      <FilterBar />

      {/* Separator Line */}
      <div
        css={css`
          height: 1px;
          background-color: ${colors.SECONDARY_200};
          margin-bottom: 16px;
        `}
      />

      {/* Content Area */}
      <div
        css={css`
          display: flex;
          flex-direction: column;
          width: 100%;
          min-width: 0;
          overflow: hidden;
        `}
      >
        {loadingGames || loadingDates || !datesLoaded ? (
          // Loading state
          <div
            css={css`
              display: flex;
              align-items: center;
              justify-content: center;
              padding: 48px 24px;
              min-height: 300px;
            `}
          >
            <Spin size='small' />
          </div>
        ) : games.length === 0 ? (
          // Empty state
          <div
            css={css`
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              padding: 48px 24px;
              min-height: 300px;
              background-color: ${colors.SECONDARY_400};
              border-radius: 8px;
            `}
          >
            <Text
              variant='paragraphLarge'
              weight='semiBold'
              color={colors.WHITE}
              style='margin-bottom: 8px;'
            >
              No Games Found
            </Text>
            <Text
              variant='paragraphMedium'
              weight='regular'
              color={colors.NEUTRAL_50}
            >
              Try Adjusting Your Filters To See More Results
            </Text>
          </div>
        ) : (
          // Games list
          <>
            <Mobile>
              {(isMobile) => {
                return Object.keys(dates).map((date) => {
                  const listOfGames = dates[date]
                  return (
                    <DayOfGames
                      key={date}
                      date={date}
                      games={listOfGames}
                      gameStatusTypes={gameStatusTypes}
                      gameTypes={gameTypes}
                      isMobile={isMobile}
                      scheduleId={null}
                      isMySchedule={true}
                    />
                  )
                })
              }}
            </Mobile>
            {/* Infinite scroll footer */}
            <div
              css={css`
                display: flex;
                justify-content: center;
                align-items: center;
                padding: 24px 0;
                min-height: 60px;
              `}
            >
              {isAppending && (
                <div
                  css={css`
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                  `}
                >
                  <Spin size='small' />
                  <Text
                    variant='paragraphSmall'
                    weight='regular'
                    color={colors.NEUTRAL_50}
                    style='font-size: 14px; line-height: 1;'
                  >
                    Loading More...
                  </Text>
                </div>
              )}
              {!hasMore &&
                !loadingGames &&
                games.length > 0 &&
                gamesPagination &&
                gamesPagination.total_pages > 1 && (
                  <Text
                    variant='paragraphSmall'
                    weight='regular'
                    color={colors.NEUTRAL_50}
                    style='font-size: 14px;'
                  >
                    No More Results
                  </Text>
                )}
            </div>
          </>
        )}
      </div>
      <FloatButton.BackTop
        icon={<ArrowUpOutlined />}
        css={css`
          .ant-float-btn-body {
            background-color: ${colors.PRIMARY} !important;
          }
          .ant-float-btn-icon {
            color: ${colors.NEUTRAL_0} !important;
          }
        `}
      />
    </div>
  )
}

const mapStateToProps = (state) => {
  return {
    gameTypes: getAllGameTypes(state),
    gameStatusTypes: getGameStatusTypesBySportId(state, null),
  }
}

export default connect(mapStateToProps)(MyScheduleList)
